import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { env } from '../../lib/env';
import { resetPasswordSchema, type ResetPasswordFormData } from '../../lib/validation';
import { useScreenReaderAnnouncement } from '@/lib/accessibility';
import { useNavigation } from '../../hooks/use-navigation';

export interface ResetPasswordFormProps {
  onSuccess?: () => void;
  redirectTo?: string;
  isSetPassword?: boolean;
}

export function ResetPasswordForm({
  onSuccess,
  redirectTo,
  isSetPassword = false,
}: ResetPasswordFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { getReturnUrl } = useNavigation();
  const announce = useScreenReaderAnnouncement();
  const actionLabel = isSetPassword ? 'Set Password' : 'Reset Password';
  const actionInProgressLabel = isSetPassword ? 'Setting Password...' : 'Resetting Password...';
  const actionDescription = isSetPassword ? 'set your password' : 'reset your password';
  const failurePrefix = isSetPassword ? 'Set password' : 'Reset password';

  // Generate unique IDs for form elements
  const passwordId = useId();
  const passwordErrorId = useId();
  const confirmPasswordId = useId();
  const confirmPasswordErrorId = useId();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    setIsSubmitting(true);

    try {
      const token = new URLSearchParams(window.location.search).get('token') ?? '';
      if (!token) {
        toast.error(
          isSetPassword
            ? 'Missing setup token. Please request a new invite link.'
            : 'Missing reset token. Please request a new reset link.'
        );
        return;
      }
      const payload = { token: token, password: data.password };

      const response = await fetch(`${env.apiBaseUrl}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `${failurePrefix} failed`);
      }

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
        return;
      }

      // Determine redirect based on CURRENT state (read fresh from store)
      // Using handleResetPasswordRedirect would use stale closure state
      const returnUrl = redirectTo || getReturnUrl();

      if (returnUrl) {
        navigate(returnUrl, { replace: true });
      } else {
        navigate('/auth/login', { replace: true });
      }
      toast.success(isSetPassword ? 'Password set' : 'Password reset');
    } catch (error) {
      if (error instanceof Error) {
        const errorMessage = error.message || `${failurePrefix} failed. Please check your link.`;
        toast.error(errorMessage);
        announce(`${failurePrefix} failed: ${errorMessage}`, 'assertive');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
      {/* Password Field */}
      <div>
        <label
          htmlFor={passwordId}
          className="block font-body text-sm font-medium text-night-700 mb-2"
        >
          Password{' '}
          <span className="text-tigerlily" aria-label="required">
            *
          </span>
        </label>
        <div className="relative">
          <Input
            id={passwordId}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Enter your password"
            {...register('password')}
            className="pr-10"
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? passwordErrorId : undefined}
            aria-required={true}
            disabled={isSubmitting}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-night-300 hover:text-night-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blueviolet-light focus-visible:ring-offset-2 rounded"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={0}
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Eye className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
        {errors.password && (
          <p id={passwordErrorId} className="mt-1 text-sm text-error" role="alert">
            {errors.password.message}
          </p>
        )}
      </div>

      {/* Confirm Password Field */}
      <div>
        <label
          htmlFor={confirmPasswordId}
          className="block font-body text-sm font-medium text-night-700 mb-2"
        >
          Confirm Password{' '}
          <span className="text-tigerlily" aria-label="required">
            *
          </span>
        </label>
        <div className="relative">
          <Input
            id={confirmPasswordId}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Enter your password"
            {...register('confirmPassword')}
            className="pr-10"
            aria-invalid={!!errors.confirmPassword}
            aria-describedby={errors.confirmPassword ? confirmPasswordErrorId : undefined}
            aria-required={true}
            disabled={isSubmitting}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-night-300 hover:text-night-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blueviolet-light focus-visible:ring-offset-2 rounded"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={0}
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Eye className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
        {errors.confirmPassword && (
          <p id={confirmPasswordErrorId} className="mt-1 text-sm text-error" role="alert">
            {errors.confirmPassword.message}
          </p>
        )}
      </div>
      {/* Submit Button */}
      <Button
        type="submit"
        className="w-full"
        disabled={isSubmitting}
        size="lg"
        aria-describedby="resetPassword-button-description"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
            {actionInProgressLabel}
          </>
        ) : (
          actionLabel
        )}
      </Button>
      <span id="resetPassword-button-description" className="sr-only">
        Submit the form to {actionDescription}
      </span>
    </form>
  );
}
